import classNames from 'classnames';
import { DateTime } from 'luxon';
import * as React from 'react';
import {
	Field,
	GenericField,
	WrappedFieldInputProps,
	WrappedFieldProps,
} from 'redux-form';

import { FormGroup, Select } from '@app/components/forms';
import {
	examRequired,
	programRequired,
	countryRequired,
	stateRequired,
	testingCenterRequired,
} from '@app/components/forms/validations.constants';
import { Notification } from '@app/components/notification';
import { FormValues } from '@app/interfaces/form';
import { LabelValue } from '@app/interfaces/labelValue';

import {
	ExamData,
	ExamDataOption,
	ExamOption,
	PropExamRadio,
	PropExamRadioGroup,
	TestingLocationFields,
} from './purchase-form.types';
import { formUtil } from './purchase-form.util';

type PropTestingLocationRadio = {
	description: string;
	data: PropExamRadio;
	input: WrappedFieldInputProps;
	onChange: React.ChangeEventHandler<HTMLInputElement>;
	children?: React.ReactNode;
};

/**
 * Exam selection component. Can be hidden if only one option available.
 *
 * @param {object}    options Exam options for select.
 * @param {string}    name    Exam field name.
 * @callback onChange         Triggers on select option change.
 * @callback validate         Validates selected option.
 *
 * @return {object} ExamSelection component.
 */
const ExamSelection = ({
	examOption,
	name,
	onChange,
	validate,
}: {
	examOption: ExamOption[];
	name: string;
	onChange?: React.ChangeEventHandler<HTMLInputElement>;
	validate: any;
}) => (
	<Select
		className={classNames('purchase-tabs__exam-option', {
			'u-hidden': examOption.length === 1,
		})}
		id={name}
		label="EXAM"
		name={name}
		onChange={onChange}
		options={examOption}
		validate={validate}
		sortByLabel
	/>
);

/**
 * Generic selection component.
 *
 * @param {boolean} disabled  Disables component.
 * @param {boolean} isLoading Sets component to loading status.
 * @param {string}  label     Component user-facing descriptor.
 * @param {string}  name      Field name.
 * @callback onChange         Triggers on select option change
 * @param {object}   options  Options for select.
 * @callback validate         Validates selected option.
 *
 * @return {object} OptionSelection component.
 */
const OptionSelection = ({
	disabled,
	isLoading,
	label,
	name,
	onChange,
	options,
	validate,
}: {
	disabled: boolean;
	isLoading: boolean;
	label: string;
	name?: string;
	onChange: React.ChangeEventHandler<HTMLInputElement>;
	options: ExamDataOption[];
	validate: any;
}) => (
	<Select
		className="purchase-tabs__exam-option"
		id={name}
		label={label}
		name={name}
		onChange={onChange}
		options={options}
		isLoading={isLoading}
		disabled={disabled}
		validate={validate}
	/>
);

/**
 * Program selection component.
 *
 * @param {boolean} disabled  Disables component.
 * @param {boolean} isLoading Sets component to loading status.
 * @param {string}  name      Program field name.
 * @param {object}   options  Options for select.
 * @callback validate         Validates selected option.
 *
 * @return {object} ProgramSelection component.
 */
const ProgramSelection = ({
	disabled,
	isLoading,
	name,
	programOptions,
	validate,
}: {
	disabled: boolean;
	isLoading: boolean;
	name: string;
	programOptions: ExamDataOption[];
	validate: any;
}) => (
	<Select
		className="purchase-tabs__exam-option"
		id={name}
		label="What program are you currently working toward"
		name={name}
		options={programOptions}
		disabled={disabled}
		isLoading={isLoading}
		validate={validate}
		sortByLabel
	/>
);

/**
 * Radio component for each of the Testing Location Types.
 *
 * Child nodes are contained within a fieldset for additional form structure.
 *
 * @param {object} children     React.node
 * @param {string} description  Description for selection.
 * @param {object} examOption   Options for underlying exams.
 * @param {object} input        Current select input properties.
 * @callback onChange           Triggers when radio option is changed within group
 *
 * @return {object} TestingLocationType component.
 */
const TestingLocationRadio: React.FC<PropTestingLocationRadio> = ({
	children,
	description,
	data: { label, value: radioValue, options = [] },
	input,
	onChange,
}: React.PropsWithChildren<PropTestingLocationRadio>) => {
	const id = `${input.name}-${label.toLowerCase()}`;
	const checked = radioValue === input.value;

	function combineRange<T extends number | string>(
		values: T[],
		formatValue: (n: T | undefined) => string,
	): string {
		const { min, max } = values.reduce(
			(acc: { min?: T; max?: T }, value) => {
				if (value !== undefined) {
					if (acc.min === undefined || value < acc.min) acc.min = value;
					if (acc.max === undefined || value > acc.max) acc.max = value;
				}
				return acc;
			},
			{ min: undefined, max: undefined },
		);
		return min === max
			? formatValue(min)
			: `${formatValue(min)} - ${formatValue(max)}`;
	}

	const currentPrice = combineRange<number>(
		options
			.filter((option) => option.currentPrice)
			.map((option) => option.currentPrice),
		(value) => (value ? `$${value}` : ''),
	);
	const salePrice = combineRange<number>(
		options
			.filter((option) => option.salePrice)
			.map((option) => option.salePrice),
		(value) => (value ? `$${value}` : ''),
	);

	/**
	 * TODO: put this back when prices issue is fixed.
	 * Ticket: https://theinstitutes.atlassian.net/browse/OPEX-1211
	 */
	// const saleEndDate = combineRange<string>(
	// options
	// .filter((option) => option.saleEndDate)
	// .map((option) => option.saleEndDate || ''),
	// (value) => (value ? formatDateString(value, 'MMMM d') : ''),
	// );

	const handleOnChange: React.ChangeEventHandler<HTMLInputElement> = (
		event,
	) => {
		input.onChange(event);
		onChange(event);
	};

	const examWindowStartDates = options.map((option) => option.examWindowStartDate);
	const shouldShowSalePrice = examWindowStartDates.some((examWindowStartDate) => {
		if (examWindowStartDate) {
			const examDateTime = DateTime.fromFormat(examWindowStartDate, 'yyyy-MM-dd');
			return DateTime.now().toMillis() < examDateTime.toMillis();
		}
		return false;
	});

	/* eslint-disable react/jsx-props-no-spreading */
	return (
		<div className="custom__rb custom__rb--gray purchase-tabs__radio">
			<input
				{...input}
				checked={checked}
				id={id}
				name={input.name}
				required
				type="radio"
				value={radioValue}
				onChange={handleOnChange}
			/>
			<label htmlFor={id}>
				{label}{' '}
				{shouldShowSalePrice ? (
					<>
						<span className="purchase-tabs__discount">({currentPrice})</span>{' '}
						<span className="u-color-primary-red">
							{salePrice}
							{/**
							 * TODO: put this back when prices issue is fixed.
							 * Ticket: https://theinstitutes.atlassian.net/browse/OPEX-1211
							*/}
							{/* {saleEndDate && ` through ${saleEndDate}`} */}
						</span>
					</>
				) : (
					currentPrice && <>({currentPrice})</>
				)}
				<br />
				<span className="purchase-tabs__product-info">
					<p className="u-margin-hug">{description}</p>
				</span>
			</label>
			<fieldset
				className={classNames(
					'purchase-tabs__product-info',
					'purchase-tabs__product-info--form',
					{
						'u-hidden': !checked,
					},
				)}
			>
				{children}
			</fieldset>
		</div>
	);
	/* eslint-enable react/jsx-props-no-spreading */
};

type PropTestingLocationForm = {
	className: string;
	examOptions: PropExamRadioGroup;
	examData: ExamData;
	formValues: FormValues;
	handleOnChange: React.ChangeEventHandler<HTMLInputElement>;
	id: string;
	isSrOnly: boolean;
	label: string;
	required: boolean;
	testingLocationFields: TestingLocationFields;
	name: string;
	/** Whether program selection is required (for PPA exam enrollments) */
	programRequired?: boolean;
};

/**
 * Component to be passed into Redux 'Field' component.
 *
 * Contains radio options for Testing Location Types.
 *
 * @param {object}  centers     Testing centers (keys: options, loading).
 * @param {string}  className   Class names.
 * @param {object}  countries   Countries (keys: options, loading).
 * @param {object}  examOptions Exam options split out into location types.
 * @param {object}  formValues  Current form values.
 * @callback handleOnChange     Triggers on field change.
 * @param {object}  input       Current select input properties.
 * @param {boolean} isSrOnly    Is screen ready only.
 * @param {string}  label       Label for radio group.
 * @param {object}  meta        Meta data from Redux 'Field' component and form.
 * @param {object}  programs    Programs (keys: options, loading)
 * @param {boolean} required    Required form element
 * @param {object}  states      States (key: options, loading, show)
 * @param {object}  testingLocationFields Testing location fields.
 *
 * @return TestingLocationFormComponent component.
 */
export const TestingLocationFormComponent: React.ComponentType<
	WrappedFieldProps & PropTestingLocationForm
> = ({
	className,
	examOptions,
	examData,
	formValues,
	handleOnChange,
	isSrOnly,
	label,
	required,
	testingLocationFields,
	programRequired: isProgramRequired = true,
	...wrappedFieldProps
}: WrappedFieldProps & PropTestingLocationForm) => {
	const { centers, countries, states, programs } = examData;
	const { meta, input } = wrappedFieldProps;

	// Conditionally apply program validator based on PPA rules
	const programValidator = isProgramRequired ? programRequired : undefined;

	const {
		PROMETRIC: prometricRadioProps,
		ONSITE: onsiteRadioProps,
		VIRTUAL: virtualRadioProps,
	} = examOptions;

	const {
		PROMETRIC: PROMETRIC_FIELDS,
		ONSITE: ONSITE_FIELDS,
		VIRTUAL: VIRTUAL_FIELDS,
	} = testingLocationFields;

	const hasError = meta.touched && meta.error;

	const isActive = (option: LabelValue) => option.value === input.value;

	return (
		<FormGroup
			className={className}
			id={input.name}
			label={label}
			required={required}
			isSrOnly={isSrOnly}
			hasError={hasError}
			col={undefined}
			disabled={false}
			readOnly={false}
			tooltipMessage=""
			isLoading={false}
		>
			{virtualRadioProps.options.length > 0 && (
				<TestingLocationRadio
					description="Take your exam virtually."
					data={virtualRadioProps}
					input={input}
					onChange={handleOnChange}
				>
					{isActive(virtualRadioProps) && (
						<>
							<ExamSelection
								name={VIRTUAL_FIELDS.EXAM_FIELDNAME}
								examOption={virtualRadioProps.options}
								validate={examRequired}
							/>
							<ProgramSelection
								name={VIRTUAL_FIELDS.PROGRAM_FIELDNAME}
								programOptions={programs.options}
								isLoading={programs.loading}
								disabled={
									!formUtil.fieldExists(
										formValues,
										VIRTUAL_FIELDS.EXAM_FIELDNAME,
									)
								}
								validate={programValidator}
							/>
						</>
					)}
				</TestingLocationRadio>
			)}

			{onsiteRadioProps.options.length > 0 && (
				<TestingLocationRadio
					description="Take your exam in person if your company is an approved, on-site testing center."
					data={onsiteRadioProps}
					input={input}
					onChange={handleOnChange}
				>
					{isActive(onsiteRadioProps) && (
						<>
							<ExamSelection
								name={ONSITE_FIELDS.EXAM_FIELDNAME}
								examOption={onsiteRadioProps.options}
								onChange={handleOnChange}
								validate={examRequired}
							/>
							<fieldset>
								<div className="purchase-tabs__exam-option-group">
									<OptionSelection
										name={ONSITE_FIELDS.COUNTRY_FIELDNAME}
										options={countries.options}
										isLoading={countries.loading}
										disabled={
											!formUtil.fieldExists(
												formValues,
												ONSITE_FIELDS.EXAM_FIELDNAME,
											)
										}
										onChange={handleOnChange}
										label="COUNTRY"
										validate={countryRequired}
									/>
									{states.show && (
										<OptionSelection
											name={ONSITE_FIELDS.STATE_FIELDNAME}
											options={states.options}
											isLoading={states.loading}
											disabled={
												!formUtil.fieldExists(
													formValues,
													ONSITE_FIELDS.COUNTRY_FIELDNAME,
												)
											}
											onChange={handleOnChange}
											label="STATE"
											validate={stateRequired}
										/>
									)}
								</div>
							</fieldset>
							<OptionSelection
								name={ONSITE_FIELDS.CENTER_FIELDNAME}
								options={centers.options}
								isLoading={centers.loading}
								disabled={
									!formUtil.fieldExists(
										formValues,
										ONSITE_FIELDS.COUNTRY_FIELDNAME,
									) ||
									(!!states.show &&
										!formUtil.fieldExists(
											formValues,
											ONSITE_FIELDS.STATE_FIELDNAME,
										))
								}
								onChange={handleOnChange}
								label="TESTING CENTER"
								validate={testingCenterRequired}
							/>
							<ProgramSelection
								name={ONSITE_FIELDS.PROGRAM_FIELDNAME}
								programOptions={programs.options}
								isLoading={programs.loading}
								disabled={
									!formUtil.fieldExists(
										formValues,
										ONSITE_FIELDS.CENTER_FIELDNAME,
									)
								}
								validate={programValidator}
							/>
							{centers.note && (
								<Notification className="u-margin-bottom">
									{centers.note}
								</Notification>
							)}
						</>
					)}
				</TestingLocationRadio>
			)}

			{prometricRadioProps.options.length > 0 && (
				<TestingLocationRadio
					description="Take your exam in person at a Prometric Testing Center."
					data={prometricRadioProps}
					input={input}
					onChange={handleOnChange}
				>
					{isActive(prometricRadioProps) && (
						<>
							<ExamSelection
								name={PROMETRIC_FIELDS.EXAM_FIELDNAME}
								examOption={prometricRadioProps.options}
								validate={examRequired}
							/>
							<ProgramSelection
								name={PROMETRIC_FIELDS.PROGRAM_FIELDNAME}
								programOptions={programs.options}
								isLoading={programs.loading}
								disabled={
									!formUtil.fieldExists(
										formValues,
										PROMETRIC_FIELDS.EXAM_FIELDNAME,
									)
								}
								validate={programValidator}
							/>
						</>
					)}
				</TestingLocationRadio>
			)}
			{hasError && (
				<p className="u-margin-hug-bottom input__error-message">{meta.error}</p>
			)}
		</FormGroup>
	);
};

/**
 * Field component that displays radio options with subsequent select options to choose from.
 *
 * @param {object} props Field properties
 *
 */
export const TestingLocationForm = Field as new () => GenericField<PropTestingLocationForm>;
